/**
 * Evaluate a Godot `Curve` at an offset. Each span is the engine's cubic Bézier, with control
 * points a third of the span apart and lifted by the tangents, not the equivalent Hermite
 * spline, so the arithmetic matches Godot's and not only its exact maths.
 *
 * Derived from Godot Engine (`scene/resources/curve.cpp`, `Curve::sample`,
 * `Curve::sample_local_nocheck`, `Curve::get_index`, and
 * `Math::bezier_interpolate`), used under the MIT licence:
 *
 *   Copyright (c) 2014-present Godot Engine contributors (see AUTHORS.md).
 *   Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 *
 *   Permission is hereby granted, free of charge, to any person obtaining
 *   a copy of this software and associated documentation files (the
 *   "Software"), to deal in the Software without restriction, including
 *   without limitation the rights to use, copy, modify, merge, publish,
 *   distribute, sublicense, and/or sell copies of the Software, and to
 *   permit persons to whom the Software is furnished to do so, subject to
 *   the following conditions:
 *
 *   The above copyright notice and this permission notice shall be
 *   included in all copies or substantial portions of the Software.
 *
 *   THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 *   EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 *   MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 *   IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 *   CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 *   TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 *   SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 *
 * See THIRD-PARTY-NOTICES.md.
 */
import { CMP_EPSILON, bezierInterpolatef } from '../../../godot/math/math.js';
import { storedReal } from '../../../godot/math/real.js';
/**
 * `Curve::sample`: the value at `offset`. An out-of-range offset clamps to the first
 * or last point's value. An empty curve answers 0, which a caller must not confuse
 * with a flat 1. The arithmetic is single precision, as Godot's `real_t` is: the offset, each
 * subtraction and each step of the Bézier round to a float.
 */
export function sampleCurve(curve, offset) {
    const points = curve.points;
    if (points.length === 0)
        return 0;
    if (points.length === 1)
        return points[0].position.y;
    const at = storedReal(offset);
    const index = curveIndex(points, at);
    if (index === points.length - 1)
        return points[index].position.y;
    const local = storedReal(at - points[index].position.x);
    if (index === 0 && local <= 0)
        return points[0].position.y;
    return sampleLocalNoCheck(points, index, local);
}
/**
 * `Curve::get_index`: a lower-bound binary search for the span containing `offset`.
 * It answers the last index past the end and 0 before the start. Exported for
 * `pointCount.ts`, which seats a padded point through it, as `Curve::_add_point` does.
 */
export function curveIndex(points, offset) {
    let imin = 0;
    let imax = points.length - 1;
    while (imax - imin > 1) {
        const middle = (imin + imax) >> 1;
        const a = points[middle].position.x;
        const b = points[middle + 1].position.x;
        if (a < offset && b < offset)
            imin = middle;
        else if (a > offset)
            imax = middle;
        else
            return middle;
    }
    return offset > points[imax].position.x ? imax : imin;
}
/**
 * `Curve::sample_local_nocheck`: the span between `index` and `index + 1`,
 * `localOffset` measured from the left point in curve units (not normalised).
 */
function sampleLocalNoCheck(points, index, localOffset) {
    const a = points[index];
    const b = points[index + 1];
    const span = storedReal(b.position.x - a.position.x);
    if (Math.abs(span) < CMP_EPSILON)
        return b.position.y;
    const t = storedReal(localOffset / span);
    // The control points are placed at a third of the span; the tangents are
    // slopes, so the vertical lift is `slope * (span / 3)`.
    const third = storedReal(span / 3);
    const yac = storedReal(a.position.y + storedReal(third * a.rightTangent));
    const ybc = storedReal(b.position.y - storedReal(third * b.leftTangent));
    return bezierInterpolatef(a.position.y, yac, ybc, b.position.y, t);
}
//# sourceMappingURL=sample.js.map